import {
  TableHead as MuiTableHead,
  TableRow,
  TableCell,
  Checkbox,
  Stack,
  Typography,
  SxProps,
  TableSortLabel,
} from "@mui/material";
import { ChangeEvent, MouseEvent, ReactNode } from "react";

import { TableColumn, TableColumnAction, TableOptions, TableOrder, TableOrderBy, TableProps } from "../..";
import { getDefaultOptions } from "../../helpers";

import { theme } from "@/theme";

interface TableHeadProps<TRecord> extends Pick<TableProps<TRecord>, "data" | "columns" | "renderDetailPanel"> {
  selected: TRecord[];
  onSelectAll?: (event: ChangeEvent<HTMLInputElement>) => void;
  columnActions?: TableColumnAction<TRecord>[];
  selectActions?: ReactNode;
  order?: TableOrder;
  orderBy?: TableOrderBy;
  onSort?: (event: MouseEvent<HTMLSpanElement>, field: TableColumn<TRecord>["field"]) => void;
  options?: Partial<
    Pick<TableOptions<TRecord>, "headStyle" | "selectable" | "showSelectAll" | "size" | "getColumnStyles" | "sortable">
  >;
}

export function TableHead<TRecord>({
  data,
  columns,
  selected,
  onSelectAll,
  columnActions = [],
  renderDetailPanel,
  selectActions,
  order = "asc",
  orderBy,
  onSort,
  options,
}: TableHeadProps<TRecord>) {
  const defaultOptions = getDefaultOptions<TRecord>(columns, options);
  const {
    selectable = defaultOptions.selectable,
    showSelectAll = defaultOptions.showSelectAll,
    headStyle = defaultOptions.headStyle,
    size = defaultOptions.size,
    sortable = defaultOptions.sortable,
    getColumnStyles = defaultOptions.getColumnStyles,
  } = options ?? defaultOptions;

  return (
    <MuiTableHead sx={headStyle}>
      <TableRow>
        {selectable && showSelectAll && (
          <TableCell padding="checkbox">
            <Checkbox
              color="primary"
              size={size}
              indeterminate={selected.length > 0 && selected.length < data.length}
              checked={data.length > 0 && selected.length === data.length}
              onChange={onSelectAll}
            />
          </TableCell>
        )}
        {selectable && !showSelectAll && <TableCell />}
        {selected.length > 0 && showSelectAll ? (
          <>
            <TableCell
              sx={{ textTransform: "lowercase", color: "primary.main" }}
              colSpan={
                columns.length + (selectable ? 1 : 0) + (columnActions.length ? 1 : 0) + (renderDetailPanel ? 1 : 0)
              }
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="subtitle2">{selected.length} selected</Typography>
                {selectActions}
              </Stack>
            </TableCell>
          </>
        ) : (
          <>
            {renderDetailPanel && <TableCell />}
            {columns.map((column, index) => {
              const { title = "", field, disableSort, format, cellStyle: columnCellStyle = {} } = column;
              return (
                <TableCell
                  key={`${field}-${index}`}
                  sx={
                    {
                      ...getColumnStyles(column),
                      ...columnCellStyle,
                      ...(format === "currency" && { textAlign: "right" }),
                    } as SxProps<typeof theme>
                  }
                >
                  <TableSortLabel
                    disabled={!sortable || disableSort || !title}
                    hideSortIcon={!sortable || disableSort || !title}
                    active={orderBy === field}
                    direction={orderBy === field ? order : "asc"}
                    onClick={(event) => {
                      if (onSort) {
                        onSort(event, field);
                      }
                    }}
                  >
                    {title}
                  </TableSortLabel>
                </TableCell>
              );
            })}
            {columnActions.length > 0 && <TableCell />}
          </>
        )}
      </TableRow>
    </MuiTableHead>
  );
}
